import { create } from "zustand";
import { inventoryService } from "./inventoryService";
import { CombinedInventoryState } from "./types";
import { mapInventoriesToBasketItems } from "../../utils/createNewOrderUtils";
import { BasketItem } from "../orders/itemTypes";

export const useInventoryStore = create<CombinedInventoryState>((set, get) => ({
  inventories: [],
  basketItems: [],
  searchText: "",
  filteredItems: [],
  isLoading: false,
  error: null,

  fetchInventories: async () => {
    set({ isLoading: true, error: null });

    try {
      const inventories = await inventoryService.fetchInventories();
      const basketItems = mapInventoriesToBasketItems(inventories);
      const filteredItems = getFilteredItems(basketItems, "");
      set({ inventories, basketItems, filteredItems, isLoading: false });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch inventory items";
      set({
        inventories: [],
        basketItems: [],
        filteredItems: [],
        isLoading: false,
        error: message,
      });
    }
  },

  setSearchText: (text: string) => {
    const state = get();
    const filteredItems = getFilteredItems(state.basketItems, text);
    set({ searchText: text, filteredItems });
  },

  updateServiceQuantity: (itemId: string, serviceId: string, delta: number) => {
    const state = get();
    const updatedBasketItems = state.basketItems.map((item) => {
      if (item.id !== itemId) {
        return item;
      }

      return {
        ...item,
        services: item.services.map((service) =>
          service.id === serviceId
            ? { ...service, qty: Math.max(0, service.qty + delta) }
            : service,
        ),
      };
    });

    const filteredItems = getFilteredItems(
      updatedBasketItems,
      state.searchText,
    );

    set({ basketItems: updatedBasketItems, filteredItems });
  },

  resetQuantities: () => {
    const { basketItems, searchText } = get();
    const updatedBasketItems = basketItems.map((item) => ({
      ...item,
      services: item.services.map((svc) => ({ ...svc, qty: 0 })),
    }));

    set({
      basketItems: updatedBasketItems,
      filteredItems: getFilteredItems(updatedBasketItems, searchText),
    });
  },
}));

const getFilteredItems = (
  items: BasketItem[],
  searchText: string,
): BasketItem[] => {
  return items.filter((item) => {
    const searchMatch = item.title
      .toLowerCase()
      .includes(searchText.trim().toLowerCase());
    return searchMatch;
  });
};
